import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, RotateCcw, Smartphone, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";

const CONFIG = {
  success: {
    icon: CheckCircle2, color: "text-emerald-400", ring: "border-emerald-500/40", glow: "shadow-[0_0_60px_rgba(16,185,129,0.25)]",
    title: "Premium Unlocked", testid: "payment-success",
    desc: "Your subscription is active. Return to the ASCENDXP app — your premium is ready and your profile will refresh automatically.",
  },
  failure: {
    icon: XCircle, color: "text-red-400", ring: "border-red-500/40", glow: "shadow-[0_0_60px_rgba(239,68,68,0.2)]",
    title: "Payment Failed", testid: "payment-failure",
    desc: "We couldn't process your payment. No charge was made. You can retry safely below.",
  },
  cancel: {
    icon: RotateCcw, color: "text-amber-400", ring: "border-amber-500/40", glow: "shadow-[0_0_60px_rgba(245,158,11,0.2)]",
    title: "Checkout Cancelled", testid: "payment-cancel",
    desc: "You cancelled before completing payment. Your selected plan is saved — pick up where you left off anytime.",
  },
};

export default function PaymentResult({ status }) {
  const cfg = CONFIG[status];
  const [params] = useSearchParams();
  const plan = params.get("plan") || "pro";
  const cycle = params.get("cycle") || "monthly";
  const Icon = cfg.icon;

  useEffect(() => {
    if (status === "success") {
      const t = setTimeout(() => { try { window.location.href = "ascend://payment-success"; } catch {} }, 800);
      return () => clearTimeout(t);
    }
  }, [status]);

  return (
    <div data-testid={cfg.testid} className="min-h-screen grid place-items-center px-6 relative pt-20">
      <div className="absolute inset-0 grid-overlay opacity-40" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className={`relative glass rounded-3xl p-10 sm:p-14 max-w-lg w-full text-center border ${cfg.ring} ${cfg.glow}`}
      >
        <Seo title={`${cfg.title} — ASCENDXP`} />
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}
          className="mx-auto w-20 h-20 grid place-items-center"
        >
          <Icon className={`w-20 h-20 ${cfg.color}`} />
        </motion.div>
        <h1 className="mt-6 font-heading font-black text-3xl sm:text-4xl tracking-tight">{cfg.title}</h1>
        <p className="mt-4 text-zinc-400 leading-relaxed">{cfg.desc}</p>

        <div className="mt-9 flex flex-col gap-3">
          {status === "success" && (
            <a href="ascend://payment-success" data-testid="open-app-btn" className="btn-gold w-full">
              <Smartphone className="w-5 h-5" /> Open ASCENDXP App
            </a>
          )}
          {status === "failure" && (
            <Link to={`/checkout?plan=${plan}&cycle=${cycle}`} data-testid="retry-btn" className="btn-primary w-full">
              <RotateCcw className="w-5 h-5" /> Retry Payment
            </Link>
          )}
          {status === "cancel" && (
            <Link to={`/checkout?plan=${plan}&cycle=${cycle}`} data-testid="resume-btn" className="btn-primary w-full">
              Resume Checkout <ArrowRight className="w-5 h-5" />
            </Link>
          )}
          <Link to="/" className="btn-ghost w-full">Back to Home</Link>
        </div>
      </motion.div>
    </div>
  );
}
